import "./App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
// Route manifest only (generated by scripts/generate-sitemap.js) — keeps page-content data out of the main bundle.
import { ALL_BRAND_SLUGS as CORE_BRAND_SLUGS, ALL_SYMPTOM_SLUGS, ALL_HUB_SLUGS, ALL_COMMERCIAL_SLUGS, ALL_CITY_SLUGS, ALL_SERVICE_CITY_SLUGS } from "./data/slugs.generated";

const ALL_BRAND_SLUGS = [...CORE_BRAND_SLUGS, ...ALL_SYMPTOM_SLUGS];
import RouteTracker from "./components/RouteTracker";
const AboutPage = lazy(() => import("./pages/AboutPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
import MobileClickToChat from "./components/MobileClickToChat";

// Code-split: each page is loaded only when needed, keeping initial bundle tiny.
import HomePage from "./pages/HomePage";
const BlogPage = lazy(() => import("./pages/BlogPage"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const FreeChecklistPage = lazy(() => import("./pages/FreeChecklistPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CommercialRefrigerationPage = lazy(() => import("./pages/CommercialRefrigerationPage"));
const PreventiveMaintenancePage = lazy(() => import("./pages/PreventiveMaintenancePage"));
const CommercialServicePage = lazy(() => import("./pages/CommercialServicePage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const BrandHubPage = lazy(() => import("./pages/BrandHubPage"));
const ServiceAreaPage = lazy(() => import("./pages/ServiceAreaPage"));
const CityPage = lazy(() => import("./pages/CityPage"));
const ServiceCityPage = lazy(() => import("./pages/ServiceCityPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <RouteTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
              <Route path="/warranty" element={<Navigate to="/terms" replace />} />
              <Route path="/jobs" element={<Navigate to="/careers" replace />} />
              <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
              <Route path="/about-us.html" element={<Navigate to="/about" replace />} />
              <Route path="/about-us" element={<Navigate to="/about" replace />} />
              <Route path="/services.html" element={<Navigate to="/#services" replace />} />
              <Route path="/warranty-plan" element={<Navigate to="/contact" replace />} />
              <Route path="/washer-dryer-fix" element={<Navigate to="/washing-machine-repair" replace />} />
              <Route path="/washer-repair" element={<Navigate to="/washing-machine-repair" replace />} />
              <Route path="/fridge-repair" element={<Navigate to="/refrigerator-repair" replace />} />
              <Route path="/commercial-refrigeration" element={<CommercialRefrigerationPage />} />
              <Route path="/commercial" element={<Navigate to="/commercial-refrigeration" replace />} />
              <Route path="/commercial-refrigeration-repair" element={<Navigate to="/commercial-refrigeration" replace />} />
              <Route path="/preventive-maintenance" element={<PreventiveMaintenancePage />} />
              <Route path="/pm-plans" element={<Navigate to="/preventive-maintenance" replace />} />
              {ALL_COMMERCIAL_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<CommercialServicePage />} />
              ))}
              <Route path="/free-checklist" element={<FreeChecklistPage />} />
              <Route path="/free-maintenance-checklist" element={<Navigate to="/free-checklist" replace />} />
              <Route path="/service-areas" element={<Navigate to="/service-area" replace />} />
              <Route path="/dekalb-service-area" element={<Navigate to="/service-area" replace />} />
              <Route path="/decatur-ga" element={<Navigate to="/appliance-repair-decatur" replace />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/service-area" element={<ServiceAreaPage />} />
              {/* Legacy URL kept alive so any inbound backlinks/indexed URLs still land users on the new preferred slug. */}
              <Route path="/oven-range-repair" element={<Navigate to="/gas-stove-repairs" replace />} />
              {/* Douglasville purged per business requirement — redirect any old inbound to service-area hub */}
              <Route path="/appliance-repair-douglasville" element={<Navigate to="/service-area" replace />} />
              {ALL_CITY_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<CityPage slug={slug} />} />
              ))}
              {ALL_BRAND_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<BrandPage />} />
              ))}
              {ALL_SERVICE_CITY_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<ServiceCityPage />} />
              ))}
              {ALL_HUB_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<BrandHubPage />} />
              ))}
              {/* Catch-all 404 — MUST be last. Prevents blank pages on any unmatched URL. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <MobileClickToChat />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
